import SchemaOrg from '@/components/base/SchemaOrg';
import GuideLayout from '@/pages/guide/components/GuideLayout';

const beginnerSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Superbuy Sheet Beginner Guide: Start Shopping Smarter Today',
  description: 'New to Superbuy Sheet? This step-by-step beginner guide walks you through everything you need to know — from browsing categories to making your first organized purchase.',
  image: 'https://readdy.ai/api/search-image?query=Warm%20inviting%20flat%20lay%20photography%20of%20shopping%20essentials%20on%20a%20clean%20cream%20desk%2C%20laptop%20with%20organized%20spreadsheet%20interface%2C%20fashion%20magazine%2C%20coffee%20cup%2C%20notebook%20with%20shopping%20list%2C%20soft%20natural%20light%20from%20window%2C%20sage%20green%20and%20terracotta%20accents%2C%20cozy%20productivity%20aesthetic%2C%20high%20detail&width=1600&height=700&seq=guide-beginner-hdr01&orientation=landscape',
  author: { '@type': 'Person', name: 'Alex Carter', jobTitle: 'Shopping Research Analyst' },
  datePublished: '2026-07-01',
  dateModified: '2026-07-13',
  publisher: { '@type': 'Organization', name: 'Superbuy Sheet', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png' } },
  mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://superbuysheet.click/guide/beginner' },
};

export default function BeginnerGuide() {
  return (
    <>
      <SchemaOrg data={beginnerSchema} />
      <GuideLayout
        title="Superbuy Sheet Beginner Guide: Start Shopping Smarter Today"
        description="New to Superbuy Sheet? This step-by-step beginner guide walks you through everything you need to know — from browsing categories to making your first organized purchase."
        breadcrumbLabel="Beginner Guide"
        readTime="8 min read"
        imageUrl="https://readdy.ai/api/search-image?query=Warm%20inviting%20flat%20lay%20photography%20of%20shopping%20essentials%20on%20a%20clean%20cream%20desk%2C%20laptop%20with%20organized%20spreadsheet%20interface%2C%20fashion%20magazine%2C%20coffee%20cup%2C%20notebook%20with%20shopping%20list%2C%20soft%20natural%20light%20from%20window%2C%20sage%20green%20and%20terracotta%20accents%2C%20cozy%20productivity%20aesthetic%2C%20high%20detail&width=1600&height=700&seq=guide-beginner-hdr01&orientation=landscape"
        imageAlt="Superbuy Sheet beginner guide for new shoppers"
        internalLinks={[
          { label: 'Complete Guide', to: '/guide', icon: 'ri-book-open-line' },
          { label: 'How to Use', to: '/guide/how-to-use', icon: 'ri-guide-line' },
          { label: 'Benefits', to: '/guide/benefits', icon: 'ri-star-line' },
          { label: 'Safety Guide', to: '/guide/safety', icon: 'ri-shield-check-line' },
          { label: 'FAQ', to: '/guide/faq', icon: 'ri-question-answer-line' },
        ]}
      >
        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Welcome to Superbuy Sheet: Your Shopping Companion
          </h2>
          <p>
            If you have just discovered <strong className="text-foreground-950">Superbuy Sheet</strong>,
            you might be wondering what makes it different from the dozens of other shopping platforms
            out there. The answer is simple: organization. While most e-commerce sites scatter products
            across countless pages with inconsistent layouts, Superbuy Sheet consolidates everything
            into fourteen clearly defined, easy-to-navigate categories. This beginner guide will walk
            you through every feature so you can start shopping smarter in minutes.
          </p>
          <p>
            The beauty of <strong className="text-foreground-950">Superbuy Sheet</strong> lies in its
            simplicity. There is no account required, no complicated setup, and no learning curve. You
            can arrive at the homepage and immediately begin exploring products organized by category.
            Whether you are a seasoned online shopper or someone who finds traditional marketplaces
            overwhelming, the structured approach of Superbuy Sheet makes product discovery feel natural
            and intuitive.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Getting Started in 3 Simple Steps
          </h2>
          <div className="space-y-5">
            <div className="flex gap-4">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 font-bold text-lg">1</span>
              <div>
                <h4 className="font-semibold text-foreground-950 mb-1">Choose Your Category</h4>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  From the homepage, browse the fourteen category cards or use the dropdown selector to jump directly to the product type you are interested in. Categories range from Shoes and Hoodies to Accessories and Perfumes — covering virtually every fashion and lifestyle need.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 font-bold text-lg">2</span>
              <div>
                <h4 className="font-semibold text-foreground-950 mb-1">Browse Organized Products</h4>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Each category click takes you to our main platform at kaspreadsheet.com, where products are presented in a clean, organized layout. You can scan multiple items quickly, compare specifications, and identify your favorites without the usual browsing fatigue.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 font-bold text-lg">3</span>
              <div>
                <h4 className="font-semibold text-foreground-950 mb-1">Make Informed Purchases</h4>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Once you have found products that match your style and needs, complete your purchase on kaspreadsheet.com with confidence. The organized discovery process means you will have already compared options and verified details before reaching the checkout stage.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Tips for First-Time Superbuy Sheet Users
          </h2>
          <div className="space-y-3">
            {[
              'Start broad — browse the main category grid to get a feel for what is available before narrowing down to specific sub-categories.',
              'Use the dropdown selector for quick jumps when you already know which category you want to explore.',
              'Bookmark your favorite categories for faster access on return visits.',
              'Read the Complete Guide for deeper insights into how the Superbuy Sheet platform works and how to maximize your shopping efficiency.',
              'Check back regularly — new products are added across all categories, so there is always something fresh to discover.',
            ].map((tip, i) => (
              <div key={i} className="flex gap-3">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-xs"></i>
                </span>
                <p className="text-sm text-foreground-600 leading-relaxed">{tip}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Common Beginner Questions Answered
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-background-100/70 rounded-xl border border-background-200/50">
              <h4 className="font-semibold text-foreground-950 mb-1">Do I need to create an account?</h4>
              <p className="text-sm text-foreground-600">No. Browsing categories and discovering products on Superbuy Sheet requires no registration whatsoever. You can start exploring immediately.</p>
            </div>
            <div className="p-4 bg-background-100/70 rounded-xl border border-background-200/50">
              <h4 className="font-semibold text-foreground-950 mb-1">Is Superbuy Sheet free?</h4>
              <p className="text-sm text-foreground-600">Absolutely. The discovery platform is completely free to use. Purchases are handled through our main website at kaspreadsheet.com.</p>
            </div>
            <div className="p-4 bg-background-100/70 rounded-xl border border-background-200/50">
              <h4 className="font-semibold text-foreground-950 mb-1">How is this different from regular shopping?</h4>
              <p className="text-sm text-foreground-600">Traditional online shopping involves jumping between fragmented product pages. Superbuy Sheet organizes everything by category for faster, more efficient browsing.</p>
            </div>
          </div>
        </section>

        <section className="text-center py-6">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-base rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Start Shopping Now
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </span>
          </a>
        </section>
      </GuideLayout>
    </>
  );
}